import { ChangeDetectionStrategy, Component, computed, DestroyRef, HostListener, inject, OnInit, signal, type Signal, type WritableSignal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterLink, RouterLinkActive, type IsActiveMatchOptions } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowLeft,
  faBars,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { TranslateModule } from "@ngx-translate/core";
import { filter } from "rxjs/operators";
import { EXACT_LINK_ACTIVE_OPTIONS } from "@core/consts/link-active-options.constants";
import { CUSTOM_NAV_ICONS, type CustomNavIconsMap } from "@core/consts/custom-nav-icons.constants";
import { MOBILE_SECONDARY_MENU_INDUCTION_ITEM, MOBILE_SECONDARY_MENU_ITEMS } from "@core/consts/mobile-secondary-menu.constants";
import type { MobileSecondaryMenuItem } from "@core/models/mobile-secondary-menu-item.model";
import { DomainService } from "@shared/services/domain/domain.service";

import { ButtonDirective } from "@shared/directives/button.directive";
import { InteractiveDirective } from "@shared/directives/interactive.directive";
import { InstallAppButtonComponent } from "@shared/components/install-app-button/install-app-button.component";
import { SanitizeHtmlPipe } from "@shared/pipe/sanitize-html.pipe";
import { PwaInstallService } from "@shared/services/pwa/pwa-install.service";
const COMPACT_HEADER_MAX_WIDTH_PX: number = 768;

@Component({
  selector: "app-mobile-secondary-menu",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./mobile-secondary-menu.component.html",
  styleUrls: ["./mobile-secondary-menu.component.scss"],
  imports: [ InteractiveDirective, ButtonDirective, RouterLink, RouterLinkActive, FontAwesomeModule, TranslateModule, InstallAppButtonComponent, SanitizeHtmlPipe ],
})
export class MobileSecondaryMenuComponent implements OnInit {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly router: Router = inject(Router);
  private readonly domainService: DomainService = inject(DomainService);
  protected readonly pwaInstall: PwaInstallService = inject(PwaInstallService);

  protected readonly faBars: IconDefinition = faBars;
  protected readonly faArrowLeft: IconDefinition = faArrowLeft;
  protected readonly faChevronRight: IconDefinition = faChevronRight;

  protected readonly menuItems: Signal<readonly MobileSecondaryMenuItem[]> = computed((): readonly MobileSecondaryMenuItem[] => {
    if (!this.domainService.featureFlags()["inductionCourses"]) return MOBILE_SECONDARY_MENU_ITEMS;

    const [profile, ...rest]: readonly MobileSecondaryMenuItem[] = MOBILE_SECONDARY_MENU_ITEMS;

    return [profile, MOBILE_SECONDARY_MENU_INDUCTION_ITEM, ...rest];
  });

  protected readonly exactLinkActiveOptions: IsActiveMatchOptions = EXACT_LINK_ACTIVE_OPTIONS;
  protected readonly customNavIcons: CustomNavIconsMap = CUSTOM_NAV_ICONS;
  /** `close()` tambien lo llama la suscripcion a `NavigationEnd`, no solo el boton. */
  protected readonly isOpen: WritableSignal<boolean> = signal<boolean>(false);

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.close());

    this.destroyRef.onDestroy(() => {
      document.body.style.overflow = "";
    });
  }

  @HostListener("window:resize")
  protected onResize(): void {
    if (window.innerWidth > COMPACT_HEADER_MAX_WIDTH_PX) this.close();
  }

  protected toggle(): void {
    this.isOpen.update((isOpen: boolean): boolean => !isOpen);
    this.syncBodyScrollLock();
  }

  protected close(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.syncBodyScrollLock();
  }

  private syncBodyScrollLock(): void {
    document.body.style.overflow = this.isOpen() ? "hidden" : "";
  }
}
